import { Box, Button } from "@mui/material";
import { MRT_Cell, MRT_Row } from "material-react-table";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { Colleague } from "../../Connections/types/Colleague.type";
import { renderRequestedDate } from "../../../components/BookingRequest/booking-requests-management.partial";
import { BookingRequestsActiveRow } from "../../../components/BookingRequest/typings/booking-requests.types";
import { FleetEventRow } from "../../../components/FleetManagement/EventDetailDialog/FleetManagementEventDetailTable.component";
import { renderFleetEventEdit } from "../../../components/FleetManagement/EventDetailDialog/partial/FleetManagementEventDetailTable.partial";
import type { ResendVisitorConsentLinkTarget } from "../../../hooks/Remote/VisitConsent/useRemoteResendVisitorConsentLink";
import type { LocationInventory } from "../../FloorManager/typings/location-inventory";
import { VisitorBookingsActionsCell } from "./visitor-bookings-actions-cell.component";
import { VisitorBookingsConsentCell } from "./visitor-bookings-consent-cell.component";
import { VisitorBookingsConsentLinkCell } from "./visitor-bookings-consent-link-cell.component";
import { VisitorBookingsHostEdit } from "./visitor-bookings-host-edit.partial";
import { VisitorBookingsLocationEdit } from "./visitor-bookings-location-edit.partial";
import {
  isConferenceVisitorRegistrationRow,
  visitorBookingHostDisplay,
  visitorBookingVisitorCount,
  VisitorBookingTableRow
} from "./visitor-bookings-table.partial";
import { visitorConsentLinkRowKey } from "./visitor-consent-link.logic";

type RenderVisitorBookingsActionsCellParams = {
  row: MRT_Row<BookingScheduleInterface>;
  onDone: () => void;
  canManageRequestedAtLocation: (locationInventoryId: number | null | undefined) => boolean;
  locations: LocationInventory[] | undefined;
};

type RenderVisitorBookingsPlaceCellParams = {
  row: MRT_Row<VisitorBookingTableRow>;
  showPlaceLabel: string;
  showZoneLabel: string;
  onShowPlace: (bookingId: number, bookingType: BookingType, highlight: number) => void;
};

type RenderVisitorBookingsRequestedDateCellParams = {
  row: MRT_Row<VisitorBookingTableRow>;
  cell: MRT_Cell<VisitorBookingTableRow, unknown>;
  localeFullFormat: string;
};

type RenderVisitorBookingsDateTimeEditParams = {
  row: MRT_Row<VisitorBookingTableRow>;
  field: "start" | "end";
  draft?: Partial<VisitorBookingTableRow>;
  timezone: string;
  maxDate?: Date;
  validationError?: string;
  onClearValidation?: () => void;
  onDraftChange: (patch: Partial<VisitorBookingTableRow>) => void;
};

type RenderVisitorBookingsHostCellParams = {
  row: MRT_Row<VisitorBookingTableRow>;
  colleagues: Colleague[];
};

type RenderVisitorBookingsLocationEditParams = {
  row: MRT_Row<VisitorBookingTableRow>;
  locations: LocationInventory[] | undefined;
};

type RenderVisitorBookingsConsentLinkCellParams = {
  row: MRT_Row<VisitorBookingTableRow>;
  timezone: string;
  dateFormat: string;
  consentLinkSentRowKeys: Set<string>;
  canManageConsentLink: boolean;
  sendingRowKey: string | null;
  onSendLink: (target: ResendVisitorConsentLinkTarget, rowKey: string) => void;
};

export function renderVisitorBookingsActionsCell({
  row,
  onDone,
  canManageRequestedAtLocation,
  locations
}: RenderVisitorBookingsActionsCellParams) {
  return (
    <VisitorBookingsActionsCell
      row={row}
      onDone={onDone}
      canManageRequestedAtLocation={canManageRequestedAtLocation}
      locations={locations}
    />
  );
}

export function renderVisitorBookingsPlaceCell({
  row,
  showPlaceLabel,
  showZoneLabel,
  onShowPlace
}: RenderVisitorBookingsPlaceCellParams) {
  const placeInventoryId = row.original.bookingInventoryId;
  const bookPlace = row.original.bookPlace;
  const isConference = isConferenceVisitorRegistrationRow(row.original);

  if (isConference) {
    const zoneBookingId = row.original.noPlaceZoneBookingId;
    if (!placeInventoryId || !zoneBookingId) {
      return null;
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
        <Button
          size="small"
          onClick={() => onShowPlace(zoneBookingId, BookingType.CONFERENCEZONE, placeInventoryId)}
        >
          {showZoneLabel}
        </Button>
      </Box>
    );
  }

  if (!bookPlace) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
      {placeInventoryId ? (
        <Button
          size="small"
          onClick={() =>
            onShowPlace(
              row.original.id,
              (row.original.bookingType as BookingType | undefined) ?? BookingType.WORKPLACE,
              placeInventoryId
            )
          }
        >
          {showPlaceLabel}
        </Button>
      ) : null}
    </Box>
  );
}

export function renderVisitorBookingsRequestedDateCell({
  cell,
  localeFullFormat
}: RenderVisitorBookingsRequestedDateCellParams) {
  return renderRequestedDate(
    cell as unknown as MRT_Cell<BookingRequestsActiveRow, unknown>,
    localeFullFormat
  );
}

export function renderVisitorBookingsDateTimeEdit({
  row,
  field,
  draft,
  timezone,
  maxDate,
  validationError,
  onClearValidation,
  onDraftChange
}: RenderVisitorBookingsDateTimeEditParams) {
  return renderFleetEventEdit({
    row: row as unknown as MRT_Row<FleetEventRow>,
    title: field,
    draft: draft as Partial<FleetEventRow> | undefined,
    timezone,
    maxDate,
    validationError,
    onClearValidation,
    onDraftChange: patch => onDraftChange(patch as Partial<VisitorBookingTableRow>)
  });
}

export function renderVisitorBookingsHostCell({
  row,
  colleagues
}: RenderVisitorBookingsHostCellParams) {
  return visitorBookingHostDisplay(row.original, colleagues);
}

export function renderVisitorBookingsHostEdit({
  row,
  colleagues
}: RenderVisitorBookingsHostCellParams) {
  return (
    <VisitorBookingsHostEdit
      row={row as unknown as MRT_Row<BookingScheduleInterface>}
      colleagues={colleagues}
    />
  );
}

export function renderVisitorBookingsVisitorCountCell({
  row
}: Pick<RenderVisitorBookingsHostCellParams, "row">) {
  return visitorBookingVisitorCount(row.original);
}

export function renderVisitorBookingsLocationEdit({
  row,
  locations
}: RenderVisitorBookingsLocationEditParams) {
  return (
    <VisitorBookingsLocationEdit
      row={row as unknown as MRT_Row<BookingScheduleInterface>}
      locations={locations}
    />
  );
}

export function renderVisitorBookingsConsentCell({
  row
}: Pick<RenderVisitorBookingsHostCellParams, "row">) {
  return <VisitorBookingsConsentCell visit={row.original} />;
}

export function renderVisitorBookingsConsentLinkCell({
  row,
  timezone,
  dateFormat,
  consentLinkSentRowKeys,
  canManageConsentLink,
  sendingRowKey,
  onSendLink
}: RenderVisitorBookingsConsentLinkCellParams) {
  const visit = row.original;
  const rowKey = visitorConsentLinkRowKey({
    consentStatus: visit.consentStatus,
    linkSentManually: false,
    consentLinkSentAt: visit.consentLinkSentAt,
    placeBookingId: visit.id,
    visitorRegistrationId: visit.visitorRegistrationId
  });

  return (
    <VisitorBookingsConsentLinkCell
      visit={visit}
      timezone={timezone}
      dateFormat={dateFormat}
      linkSentManually={consentLinkSentRowKeys.has(rowKey)}
      canManageConsentLink={canManageConsentLink}
      isSending={sendingRowKey === rowKey}
      onSendLink={onSendLink}
      visitorRegistrationId={visit.visitorRegistrationId}
      rowKey={rowKey}
    />
  );
}
